import { useEffect, useReducer } from 'react'
import { connect } from 'react-redux'
import { Grid, Alert } from 'react-bootstrap'

import ScriptInject from 'components/ScriptInject'
import InlineLoader from 'components/InlineLoader'

import { browserHistory, withRouter } from 'react-router'
import { BotContainer } from 'containers'
import { selectDevices } from '../DeviceSlider/selectors'
import { updatePhoneId, swapLanguage, fetchUser } from './actions'
import HeaderContainer from '../Header'
import { selectLanguage, selectUser, selectIsFetching } from './selectors'
import { getCustomer } from 'helpers'
import { browserHistoryMutationSideEffects } from 'helpers/router'
import GlobalStyle from '../GlobalStyle'
import { OptionsHOC, TrackingHOC } from 'hocs'
import { KendraContextProvider } from '../../context'
import { NotificationContextProvider } from '../../context/NotificationContext'
import { handleDeeplink } from '../../helpers/bot'

/**
 * The `AppContainer` component is the main container component for the application. 
 * It is responsible for rendering the entire application's children components.
 *
 * It also handles user authentication, language swapping, and external link handling.
 *
 * A hook is used to fetch the user data and handle language swapping when the component mounts.
 * The component also listens for click events on links and handles them accordingly,
 * opening external links in a new tab and internal links without a page refresh.
 *
 * If the user is not fetched or is not authorized to use the CareAssist feature,
 * the component will render appropriate error messages or loading indicators.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components.
 * @param {Object} props.router - The router object.
 * @param {Object} props.user - The user object.
 * @param {boolean} props.isFetching - Whether the user data is being fetched.
 * @param {Function} props.fetchUser - The function to fetch user data.
 * @param {Object} props.location - The location object.
 * @param {Function} props.swapLanguage - The function to swap the language.
 * @returns {JSX.Element} The AppContainer component.
 */
const AppContainer = ({
  children,
  router,
  user,
  isFetching,
  fetchUser,
  location,
  swapLanguage,
  ...props
}) => {
  const customer = getCustomer()
  const { auth } = router.routes[0]
  const forceUpdate = useReducer(() => ({}), {})[1]

  useEffect(() => {
    fetchUser(auth)

    if (location.query.lang === 'es') {
      swapLanguage('es')
      router.replace({
        pathname: location.pathname,
        query: location.query
      })
      forceUpdate()
    }

    // Open external links in new tab, open internal links without refresh
    document.addEventListener('click', function (event) {
      if (event.target.hasAttribute('href')) {
        if (event.target && event.target === '_parent') {
          return
        }

        event.preventDefault()
        const regex = new RegExp(`/${window.location.host}/`)
        const url = event.target.getAttribute('href')

        if (!regex.test(event.target.href)) {
          window.open(url, '_blank')
        } else {
          browserHistory.push(url)
          browserHistoryMutationSideEffects()
          handleDeeplink()
        }
      }
    })
  }, [])

  if (Object.keys(user).length === 0 || isFetching) {
    return <InlineLoader />
  }

  if (!user.careassist || user.careassist.enabled !== 1) {
    return (
      <Alert bsStyle="danger">
        <span>
          AgentAI not enabled for the user, please contact administrator.
        </span>

        <a className="pull-right" href="#" onClick={auth.logout()}>
          Logout
        </a>
      </Alert>
    )
  }
  const generateProps = () => {
    return {
      ...props,
      user,
      auth,
      customer,
      router,
      location
    }
  }

  return (
    <div>
      <ScriptInject />
      <KendraContextProvider {...generateProps()}>
        <NotificationContextProvider>
          <HeaderContainer auth={auth} customer={customer} {...generateProps()} />
          <Grid style={{ minHeight: '50vh' }}> {children}</Grid>
          <BotContainer />
          <GlobalStyle />
        </NotificationContextProvider>
      </KendraContextProvider>
    </div>
  )
}

const mapStateToProps = state => ({
  language: selectLanguage(state),
  devices: selectDevices(state),
  query: state.routing.locationBeforeTransitions.query,
  user: selectUser(state),
  isFetching: selectIsFetching(state)
})

const mapDispatchToProps = dispatch => ({
  updatePhoneId: (phoneId, deviceName) =>
    dispatch(updatePhoneId(phoneId, deviceName)),
  swapLanguage: language => dispatch(swapLanguage(language)),
  fetchUser: auth => dispatch(fetchUser.request(auth))
})

const ConnectedAppContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withRouter(
    TrackingHOC(
      OptionsHOC(
        AppContainer
      )
    )
  )
)

export default ConnectedAppContainer
